import React, { useEffect, useRef, useState } from "react";
import { Input, message, Slider, Upload } from "antd";
import {
  CaretDownFilled,
  CloseOutlined,
  RedoOutlined,
} from "@ant-design/icons";
import DropdownV2 from "@/common/elements/DropdownV2";
import Button from "@/common/elements/Button";
import {
  StyledUploadContent,
  StyledUploadeContainer,
  StyledUploadHeader,
} from "./styles";
import { StyledFlex, StyledGridCol, StyledGridRow } from "@/common/styles";
import {
  extractPath,
  imageToBase64,
  isNonEmptyVariable,
} from "@/util/common_function.js";
import notificationService from "@/util/notificationService";

const FileUploader = ({
  browseFileType = "image",
  acceptImageFormats = "image/*",
  acceptDocFormats = "application/pdf,.pdf,.doc,.docx",
  canvasMaxHeight = "inherit",
  allowMultiple = false,
  onUpload,
  imageUrlFromParent = "",
  isEdit = false,
  onApplyFilter = null,
  layout, // default:single, split
  onCapture,
  isCaptureAllowed = false,
}) => {
  const [imageUrl, setImageUrl] = useState(null);

  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [hue, setHue] = useState(0);
  const [grayscale, setGrayscale] = useState(0);

  const [rotate, setRotate] = useState(0);

  const refDropdownV2 = useRef(null);
  const imageRef = useRef(new Image());
  const canvasRef = useRef(null);

  const [photoID, setPhotoID] = useState("");
  const [isPhotoID, setIsPhotoID] = useState(false);

  const [uploadedFile, setUploadedFile] = useState(null);

  const [fileUrl, setFileUrl] = useState(null);
  const [fileMode, setFileMode] = useState(browseFileType); // 'image' or 'document' mode

  const [fileItems, setFileItems] = useState([]);

  const [isFilterView, setIsFilterView] = useState(false);

  useEffect(() => {
    if (isNonEmptyVariable(imageUrlFromParent)) {
      setImageUrl(imageUrlFromParent);
    } else {
      setImageUrl("");
    }
  }, [imageUrlFromParent]);

  useEffect(() => {
    if (imageUrl) {
      const img = imageRef.current;
      img.src = imageUrl;
      img.onload = () => {
        applyFilters();
      };
      if (layout) {
        setIsFilterView(true);
      }
    }
  }, [imageUrl]);

  useEffect(() => {
    if (imageUrl) {
      applyFilters();
    }
  }, [brightness, contrast, hue, grayscale, rotate]);

  const handleUpload = (file) => {
    console.log(file.type.startsWith("image/"));

    if (!file.type.startsWith("image/")) {
      notificationService.error("This file format is not supported");
      return;
    }

    if (fileMode === "image" && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImageUrl(e.target.result);
        resetFilters();
      };
      reader.readAsDataURL(file);
      setFileMode("image");
    } else if (
      fileMode === "document" &&
      acceptDocFormats.split(",").includes(file.type)
    ) {
      setFileUrl(URL.createObjectURL(file));
      setFileMode("document");
      setUploadedFile(file);
    } else {
      message.error("Please upload a valid file type.");
      return false;
    }

    setUploadedFile(file);

    if (onUpload) {
      onUpload(file); // Call the parent-provided onUpload function
    }

    return false; // Prevent auto upload
  };
  const getFilteredImage = (canvas) => {
    if (canvas) {
      const dataURL = canvas?.toDataURL("image/jpg");
      return dataURL;
    }
    return null;
  };

  const applyFilters1 = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const img = imageRef.current;

    if (canvas && ctx && img.complete) {
      // Fixed canvas size (square)
      const fixedSize = 200; // you can choose any fixed dimension
      canvas.width = fixedSize;
      canvas.height = fixedSize;

      // Original image dimensions
      const imgWidth = img.naturalWidth;
      const imgHeight = img.naturalHeight;

      // Calculate bounding box for rotated image
      const angleInRadians = (rotate * Math.PI) / 180;
      const cosAngle = Math.abs(Math.cos(angleInRadians));
      const sinAngle = Math.abs(Math.sin(angleInRadians));
      // const boundingWidth = imgWidth * cosAngle + imgHeight * sinAngle;
      // const boundingHeight = imgWidth * sinAngle + imgHeight * cosAngle;

      // Update canvas dimensions
      const boundingWidth = imgWidth * cosAngle + imgHeight * sinAngle;
      const boundingHeight = imgWidth * sinAngle + imgHeight * cosAngle;

      canvas.width = boundingWidth;
      canvas.height = boundingHeight;

      // Clear the canvas and apply filters
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.filter = `
        brightness(${brightness}%)
        contrast(${contrast}%)
        hue-rotate(${hue}deg)
        grayscale(${grayscale}%)
      `;

      // Rotate and draw the image
      const scale = Math.min(fixedSize / imgWidth, fixedSize / imgHeight);
      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2); // Center the rotation
      ctx.rotate(angleInRadians); // Rotate the image
      ctx.drawImage(
        img,
        (-imgWidth / 2) * scale,
        (-imgHeight / 2) * scale,
        imgWidth * scale,
        imgHeight * scale,
      );
      ctx.restore();
      if (isNonEmptyVariable(onApplyFilter))
        onApplyFilter(getFilteredImage(canvas));
    }
  };

  const applyFilters = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const img = imageRef.current;

    if (canvas && ctx && img.complete) {
      // Fixed canvas size (square)
      const fixedSize = 200; // you can choose any fixed dimension
      canvas.width = fixedSize;
      canvas.height = fixedSize;

      // Original image dimensions
      const imgWidth = img.naturalWidth;
      const imgHeight = img.naturalHeight;

      // Scale to fit inside fixed square
      const scale = Math.min(fixedSize / imgWidth, fixedSize / imgHeight);

      // Clear and apply filters
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.filter = `
      brightness(${brightness}%)
      contrast(${contrast}%)
      hue-rotate(${hue}deg)
      grayscale(${grayscale}%)
    `;

      // Center and rotate
      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotate * Math.PI) / 180);
      ctx.drawImage(
        img,
        (-imgWidth / 2) * scale,
        (-imgHeight / 2) * scale,
        imgWidth * scale,
        imgHeight * scale,
      );
      ctx.restore();

      if (isNonEmptyVariable(onApplyFilter))
        onApplyFilter(getFilteredImage(canvas));
    }
  };

  const handleFetchImage = () => {
    const fetchedImage = `https://placehold.co/600x400/EEE/31343C`;
    setImageUrl(fetchedImage);
  };

  const resetFilters = () => {
    setBrightness(100);
    setContrast(100);
    setHue(0);
    setGrayscale(0);
    setRotate(0);
  };

  const onChangeFile = ({ file, fileList }) => {
    // Check the upload status of the file
    if (file.status !== "uploading") {
      if (allowMultiple) {
        console.log("fileList", fileList);
        setFileItems(fileList);
      }

      if (file.status === "done" || file.status === "removed") {
        handleUpload(file);
      }

      if (file.status === "error") {
        console.error("File upload failed:", file.name);
        message.error(`${file.name} upload failed.`);
      }
    }
    setTimeout(() => {
      dropdownMenu.current.blur();
    }, 0);
  };

  const dropdownMenu = {
    image: [
      {
        key: "action-browse",
        label: (
          <Upload
            accept={acceptImageFormats}
            showUploadList={false}
            beforeUpload={handleUpload}
            multiple={allowMultiple}
            onChange={onChangeFile}
            style={{ width: "100%" }}
          >
            Browse For Image
          </Upload>
        ),
      },
      {
        key: "action-capture",
        label: (
          <a
            onClick={(e) => {
              onCapture();
            }}
          >
            Capture Picture
          </a>
        ),
        disabled: !isCaptureAllowed,
      },
      // {
      //   key: "action-by-photo-id",
      //   label: <a onClick={() => setIsPhotoID(!isPhotoID)}>Enter Photo ID</a>,
      //   disabled:true
      // },
      // {
      //   key: "action-twain",
      //   label: <a>Select Twain Source</a>,
      //   disabled: true,
      // },
      {
        key: "action-toggle-filters",
        label: (
          <a onClick={() => setIsFilterView(!isFilterView)}>Images Filters</a>
        ),
        disabled: !imageUrl,
      },
    ],
    document: [
      {
        key: "action-browse",
        label: (
          <Upload
            accept={acceptDocFormats}
            showUploadList={false}
            beforeUpload={handleUpload}
            multiple={allowMultiple}
            onChange={onChangeFile}
            style={{ width: "100%" }}
          >
            Browse For Document
          </Upload>
        ),
      },
    ],
  };

  const handleDropdownChange = (openState, b) => {
    console.log("openState D2: ", openState, b);
    if (!openState) {
      setTimeout(() => {
        refDropdownV2.current.focus();
      }, 0);
    }
  };
  return (
    <StyledUploadeContainer className="styled-upload-container">
      <StyledGridRow justify="center">
        <StyledGridCol
          lg={layout === "split" ? 14 : 24}
          order={layout === "split" ? 0 : 1}
        >
          <StyledFlex
            style={{ height: "100%", width: "100%", justifyContent: "center" }}
          >
            <StyledUploadContent
              className={`styled-upload-content ${
                layout === "split" ? "split-view" : ""
              }`}
              style={{ maxHeight: canvasMaxHeight }}
            >
              <DropdownV2
                onOpenChange={handleDropdownChange}
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                className="upload-action-menu"
                ref={refDropdownV2}
                menuItems={
                  fileMode === "image"
                    ? dropdownMenu.image
                    : dropdownMenu.document
                }
                trigger={["click"]}
                content={
                  <Button
                    type="default"
                    disabled={isEdit}
                    className="btn-upload-toggler"
                    color="default"
                  >
                    <CaretDownFilled />
                  </Button>
                }
              />

              {/* Enter by PhotoID */}
              {isPhotoID && (
                <StyledFlex gap="10px" style={{ padding: "10px" }}>
                  <Input
                    placeholder="Enter PhotoID"
                    value={photoID}
                    onChange={(e) => setPhotoID(e.target.value)}
                  />
                  <Button
                    type="warning"
                    color="warning"
                    onClick={handleFetchImage}
                  >
                    Fetch Image
                  </Button>
                </StyledFlex>
              )}

              {fileItems.length > 0 ? (
                fileItems.map((fileItem) => (
                  <StyledFlex
                    justify="space-between"
                    gap="10px"
                    className="upload-list-item"
                    key={fileItem?.name}
                  >
                    <div>
                      <h2>{fileItem?.name}</h2>
                      <div className="file-info">
                        <small>{fileItem?.type}</small>
                        <small>
                          {(fileItem?.size / (1024 * 1024)).toFixed(2)} MB
                        </small>
                      </div>
                    </div>
                    <div>
                      {/* Remove file */}
                      <a className="remove-item-icon">
                        <CloseOutlined />
                      </a>
                    </div>
                  </StyledFlex>
                ))
              ) : fileMode === "image" ? (
                <>
                  <div className="view-wrapper">
                    {imageUrl ? (
                      <canvas
                        ref={canvasRef}
                        style={{ maxWidth: "100%", maxHeight: canvasMaxHeight }}
                      />
                    ) : (
                      <div className="no-file-found">No Image</div>
                    )}
                  </div>
                </>
              ) : fileMode === "document" && uploadedFile ? (
                <StyledFlex
                  justify="space-between"
                  gap="10px"
                  className="upload-list-item"
                >
                  <div>
                    <h2>{uploadedFile?.name}</h2>
                    <div className="file-info">
                      <small>{uploadedFile?.type}</small>
                      <small>
                        {(uploadedFile?.size / (1024 * 1024)).toFixed(2)} MB
                      </small>
                    </div>
                  </div>
                  <div>
                    {/* Remove file */}
                    <a className="remove-item-icon">
                      <CloseOutlined />
                    </a>
                  </div>
                </StyledFlex>
              ) : (
                <div className="no-file-found">No File Found</div>
              )}
            </StyledUploadContent>
          </StyledFlex>
        </StyledGridCol>
        {fileMode === "image" && isFilterView && !isEdit && (
          <StyledGridCol
            lg={layout === "split" ? 10 : 24}
            order={layout === "split" ? 1 : 1}
          >
            <StyledUploadHeader className="styled-upload-header">
              {/* Adjustments */}
              <div
                className={`filter ${layout === "split" ? "split-view" : ""}`}
              >
                <StyledGridRow gutter={16}>
                  <StyledGridCol span={24}>
                    <StyledFlex justify="space-between">
                      <h4 style={{ color: "var(--text-black)" }}>Filter</h4>
                      <div>
                        <a className="filter-reset-link" onClick={resetFilters}>
                          Reset Default
                        </a>

                        <Button
                          type="link"
                          style={{ marginLeft: "9px" }}
                          variant="link"
                          color="default"
                          onClick={() => setIsFilterView(false)}
                          icon={<CloseOutlined />}
                        />
                      </div>
                    </StyledFlex>
                  </StyledGridCol>

                  <StyledGridCol span={layout === "split" ? 24 : 12}>
                    <div className="filter-item">
                      <label>
                        Brightness
                        <Button
                          type="link"
                          color="default"
                          title="Reset"
                          variant="link"
                          className="btn-reset-action"
                          onClick={() => setBrightness(100)}
                        >
                          <RedoOutlined />
                        </Button>
                      </label>
                      <Slider
                        min={0}
                        max={200}
                        value={brightness}
                        onChange={setBrightness}
                        marks={{
                          0: "0%",
                          50: "50%",
                          100: "100%",
                          150: "150%",
                          200: "200%",
                        }}
                      />
                    </div>

                    <div className="filter-item">
                      <label>
                        Contrast
                        <Button
                          type="link"
                          color="default"
                          title="Reset"
                          variant="link"
                          className="btn-reset-action"
                          onClick={() => setContrast(100)}
                        >
                          <RedoOutlined />
                        </Button>
                      </label>
                      <Slider
                        min={0}
                        max={200}
                        value={contrast}
                        onChange={setContrast}
                        marks={{
                          0: "0%",
                          50: "50%",
                          100: "100%",
                          150: "150%",
                          200: "200%",
                        }}
                      />
                    </div>
                  </StyledGridCol>

                  <StyledGridCol span={layout === "split" ? 24 : 12}>
                    <div className="filter-item">
                      <label>
                        Grayscale
                        <Button
                          type="link"
                          color="default"
                          title="Reset"
                          variant="link"
                          className="btn-reset-action"
                          onClick={() => setGrayscale(0)}
                        >
                          <RedoOutlined />
                        </Button>
                      </label>
                      <Slider
                        min={0}
                        max={100}
                        value={grayscale}
                        onChange={setGrayscale}
                        marks={{
                          0: "0%",
                          25: "25%",
                          50: "50%",
                          75: "75%",
                          100: "100%",
                        }}
                      />
                    </div>

                    <div className="filter-item">
                      <label>
                        Hue
                        <Button
                          type="link"
                          color="default"
                          title="Reset"
                          variant="link"
                          className="btn-reset-action"
                          onClick={() => setHue(0)}
                        >
                          <RedoOutlined />
                        </Button>
                      </label>
                      <Slider
                        min={0}
                        max={360}
                        value={hue}
                        onChange={setHue}
                        marks={{
                          0: "0°",
                          90: "90°",
                          180: "180°",
                          270: "270°",
                          360: "360°",
                        }}
                      />
                    </div>
                  </StyledGridCol>

                  <StyledGridCol span={24} marginBottom="10px">
                    <div className="filter-item">
                      <label
                        style={{
                          paddingRight: "15px",
                          width: layout ? "100%" : "auto",
                        }}
                      >
                        Rotate (90)
                        <Button
                          type="link"
                          color="default"
                          title="Reset"
                          variant="link"
                          className="btn-reset-action"
                          onClick={() => setRotate(0)}
                        >
                          <RedoOutlined />
                        </Button>
                      </label>
                      <Button
                        type="secondary"
                        color="secondary"
                        onClick={() => setRotate((prev) => prev + 90)}
                      >
                        Clockwise
                      </Button>
                      <Button
                        type="secondary"
                        color="secondary"
                        onClick={() => setRotate((prev) => prev - 90)}
                        style={{ marginLeft: "10px" }}
                      >
                        Counter-Clockwise
                      </Button>
                    </div>
                  </StyledGridCol>
                </StyledGridRow>
              </div>
            </StyledUploadHeader>
          </StyledGridCol>
        )}
      </StyledGridRow>
    </StyledUploadeContainer>
  );
};

export default FileUploader;
